import { fetchBinFile } from 'api'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectComicOrigin, selectPictureOrigin, selectPreviewOrigin } from 'redux/selector/app'
import { unpackAesImages } from 'utils/cryoto'
import { Image, ImageWrapper, NonLazyLoadWrapper } from './Styled'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { catchPromiseCancel } from 'utils/catchPromiseCancel'

export interface AesImageProps {
  decryptKey?: any
  src?: string
  noLazy?: boolean
  initialAspectRatio?: number | null
  aspectRatio?: number | null
  onImagesLoad?: (urls: string[]) => void
  onImageBlobsLoad?: (blobs: Blob[]) => void
  draggable?: boolean
  hasPlaceholder?: boolean
  placeholder?: string | null
  source?: 'preview' | 'picture' | 'comic'
  cached?: boolean
  allowUnload?: boolean
  rootClassName?: string
  rootMargin?: string
  useExternalHeight?: boolean
  onDomUpdate?: (images: (HTMLElement | null)[]) => void
  [key: string]: any
}

function AesImage({
  decryptKey,
  src = '',
  noLazy = false,
  initialAspectRatio = null,
  aspectRatio = null,
  onImagesLoad,
  onImageBlobsLoad,
  draggable = false,
  hasPlaceholder = true,
  placeholder = null,
  // preview, picture, comic
  source = 'comic',
  cached = false,
  allowUnload = false,
  rootClassName = 'lazy-root',
  rootMargin = '100px',
  useExternalHeight = false,
  onDomUpdate = (_images: (HTMLElement | null)[]) => {},
  ...props
}: AesImageProps) {
  const previewOrigin = useSelector(selectPreviewOrigin)
  const pictureOrigin = useSelector(selectPictureOrigin)
  const comicOrigin = useSelector(selectComicOrigin)
  const currentOrigin = source === 'preview' ? previewOrigin : source === 'comic' ? comicOrigin : pictureOrigin

  const { ref, visible } = useIntersectionObserver<HTMLDivElement>({
    /* Optional options */
    defaultVisible: false,
    threshold: 0,
    triggerOnce: !allowUnload,
    rootClassName,
    rootMargin,
  })

  const cache = useRef<{ [src: string]: ArrayBuffer }>({})

  const [imageArrayBuffer, setImageArrayBuffer] = useState<ArrayBuffer | null>(null)
  const [blobs, setBlobs] = useState<Blob[]>([])
  const [urls, setUrls] = useState<string[]>([])
  const [animationFinished, setAnimationFinished] = useState(false)
  const fetchedSrc = useRef<string | null>(null)

  useEffect(() => {
    if (!currentOrigin) return

    if (src === '') {
      fetchedSrc.current = src
      return
    }

    if (noLazy || visible) {
      if (fetchedSrc.current !== src) {
        const controller = new AbortController()
        if (cached && cache.current[src]) {
          fetchedSrc.current = src
          setImageArrayBuffer(cache.current[src] as ArrayBuffer)
        }
        fetchBinFile({
          responseType: 'arraybuffer',
          url: currentOrigin.replace(/\/?$/, '/')?.concat(src.replace(/^\//, '')),
          signal: controller.signal,
        })
          .then((arraybuffer) => {
            fetchedSrc.current = src
            setImageArrayBuffer(arraybuffer as ArrayBuffer)
            if (cached) {
              cache.current[src] = arraybuffer
            }
          })
          .catch(catchPromiseCancel)

        return () => {
          controller.abort()
        }
      }
    } else {
      fetchedSrc.current = null
      setImageArrayBuffer(null)
    }
  }, [visible, noLazy, currentOrigin, src, cached])

  const decryptCache = useRef(new Map<ArrayBuffer, Blob[]>())

  useEffect(() => {
    let aborted = false
    let urls: string[] = []

    async function updateURL() {
      if (imageArrayBuffer == null) {
        setBlobs([])
        setUrls([])
        setAnimationFinished(false)
        return
      }
      let blobs: Blob[]
      if (cached && decryptCache.current.has(imageArrayBuffer)) {
        blobs = decryptCache.current.get(imageArrayBuffer)!
      } else {
        blobs = await unpackAesImages(
          imageArrayBuffer,
          decryptKey,
          [0x00, 0x01, 0x05, 0x03, 0x04, 0x05, 0x09, 0x07, 0x08, 0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x0e, 0x0f]
        )
      }

      if (aborted) return

      if (cached && !decryptCache.current.has(imageArrayBuffer)) {
        decryptCache.current.set(imageArrayBuffer, blobs)
      }

      setBlobs(blobs)
      urls = blobs.map((b) => URL.createObjectURL(b))
      setUrls(urls)
    }

    updateURL()

    return () => {
      aborted = true
      urls.forEach((u) => URL.revokeObjectURL(u))
      // Do not clear images on cleanup because it cause flicking when switch image
      // setUrls([])
    }
  }, [imageArrayBuffer, decryptKey, cached])

  useEffect(() => {
    if (blobs.length > 0) {
      onImageBlobsLoad?.(blobs)
    }
  }, [onImageBlobsLoad, blobs])

  const tryEmitLoad = useCallback(() => {
    if (urls.length > 0 && loadingImages.current.length === 0) {
      onImagesLoad?.(urls)
    }
  }, [onImagesLoad, urls])

  useEffect(() => {
    tryEmitLoad()
  }, [tryEmitLoad])

  const placeholderRef = useRef<HTMLDivElement | null>(null)
  const imageRefs = useRef<(HTMLElement | null)[]>([])

  const onImageRefUpdate = (index: number, dom: HTMLElement | null) => {
    imageRefs.current = Object.assign([], imageRefs.current, { [index]: dom })
  }

  useLayoutEffect(() => {
    if (urls.length === 0) {
      onDomUpdate([placeholderRef.current])
    } else {
      onDomUpdate(imageRefs.current)
    }
  })

  const loadingImages = useRef<HTMLImageElement[]>([])

  const onImageElRefed = useCallback(
    (el: HTMLImageElement | null) => {
      if (el == null) {
        return
      }

      if (el.complete) {
        return
      }

      const doneLoading = () => {
        el.removeEventListener('load', doneLoading)
        el.removeEventListener('error', doneLoading)

        loadingImages.current = loadingImages.current.filter((i) => i !== el)

        tryEmitLoad()
      }

      el.addEventListener('load', doneLoading)
      el.addEventListener('error', doneLoading)

      loadingImages.current = [...loadingImages.current.filter((i) => i !== el), el]
    },
    [tryEmitLoad]
  )

  const imageJSX = useMemo(() => {
    if ((aspectRatio != null || initialAspectRatio != null) && urls.length === 0) {
      // placeholder when aspect ratio is set but image not loaded yet */
      return [
        <ImageWrapper
          key={0}
          hasPlaceholder={hasPlaceholder}
          placeholder={placeholder as string | undefined}
          ref={placeholderRef}
          {...props}
          aspectRatio={initialAspectRatio ?? aspectRatio}
        >
          <NonLazyLoadWrapper
            aspectRatio={initialAspectRatio ?? aspectRatio}
            useExternalHeight={useExternalHeight}
          ></NonLazyLoadWrapper>
        </ImageWrapper>,
      ]
    }

    return urls.map((url, index) => (
      <ImageWrapper
        hasPlaceholder={hasPlaceholder}
        placeholder={placeholder as string | undefined}
        ref={(d: HTMLDivElement | null) => onImageRefUpdate(index, d)}
        key={index + '_' + url}
        {...props}
        aspectRatio={aspectRatio}
      >
        <NonLazyLoadWrapper aspectRatio={aspectRatio} useExternalHeight={useExternalHeight}>
          <Image
            animation={Boolean(hasPlaceholder && !animationFinished)}
            draggable={draggable}
            aspectRatio={aspectRatio}
            src={url}
            ref={onImageElRefed}
            onAnimationEnd={() => setAnimationFinished(true)}
          />
        </NonLazyLoadWrapper>
      </ImageWrapper>
    ))
  }, [
    animationFinished,
    aspectRatio,
    draggable,
    hasPlaceholder,
    initialAspectRatio,
    onImageElRefed,
    placeholder,
    props,
    urls,
    useExternalHeight,
  ])

  return <div ref={ref}>{imageJSX}</div>
}

export default AesImage
